/**
 * フィードバック生成の待機中に表示するめんれん先輩のイラスト。
 *
 * 既存のキャラクター画像を `frontend/public/images/waiting/` に置いて使う。
 * 追加するときは `scripts/waiting_illustrations/normalize_canvas.py` を通してから
 * コミットする（キャンバスを正方形に揃えないと、絵ごとに表示サイズがばらつく）。
 */
export interface WaitingIllustration {
  key: string;
  src: string;
  /** 装飾ではなくキャラクターの状態を伝えるため、代替テキストは空にしない。 */
  alt: string;
}

export const WAITING_ILLUSTRATIONS: readonly WaitingIllustration[] = [
  {
    key: "good01",
    src: "/images/waiting/mentai-senpai_03_good01.png",
    alt: "親指を立てて励ますめんれん先輩",
  },
  {
    key: "standing04",
    src: "/images/waiting/mentai-senpai_03_standing04.png",
    alt: "拳を上げて気合いを入れるめんれん先輩",
  },
  {
    key: "standing07",
    src: "/images/waiting/mentai-senpai_03_standing07.png",
    alt: "全力で駆けていくめんれん先輩",
  },
  {
    key: "standing09",
    src: "/images/waiting/mentai-senpai_03_standing09.png",
    alt: "ヘッドホンで音声を聞いているめんれん先輩",
  },
  {
    key: "standing10",
    src: "/images/waiting/mentai-senpai_03_standing10.png",
    alt: "ボクシンググローブを構えるめんれん先輩",
  },
] as const;

/**
 * 識別子から表示するイラストの位置を返す。
 *
 * 自己分析チャットでは先輩の発話ごとにイラストを添える。毎回の描画で乱数を引くと、
 * 状況確認の反復や再読み込みで絵が入れ替わって落ち着かないため、発話の識別子から
 * 位置を決めて同じ発話には常に同じ絵が並ぶようにする。
 *
 * @param key 発話の識別子など、絵を固定したい対象を表す文字列
 */
export function illustrationIndexForKey(key: string): number {
  const length = WAITING_ILLUSTRATIONS.length;
  if (length <= 1) return 0;

  // 文字コードの総和では並び順の違いを区別できないため、桁をずらしながら足す。
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) % 1_000_000_007;
  }
  return hash % length;
}

/**
 * 直前と必ず異なるイラストの位置を返す。
 *
 * 「ランダムに選び、直前と同じなら選び直す」形にすると候補が1枚のとき無限ループになるため、
 * 残り候補から等確率で1枚が1回の計算で決まる式にしている。
 *
 * @param currentIndex 直前に表示していた位置
 * @param random 0以上1未満の乱数（テストから差し替えられるようにする）
 */
export function nextIllustrationIndex(
  currentIndex: number,
  random: number = Math.random(),
): number {
  const length = WAITING_ILLUSTRATIONS.length;
  if (length <= 1) return 0;

  const shift = 1 + Math.floor(random * (length - 1));
  return (currentIndex + shift) % length;
}
